// Kyvykkyydet & odotettu toiminta — readiness + variants + parameters (story #1140 / #1145).
//
// A capability APPLIES based on declared variants, then reads active or blocked from readiness
// (declared signals + parameters). Declare on/off: Signaalit → Signaalien aktivointi.
// Pairing: Signaalit → Signaalien kytkennät (§2.6 / §2.7 one-editable-home).
// Missing-signal affordances deep-link to Signaalit.
// Zone behaviors stay read-only for signals.
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Wrench,
  Boxes,
  ChevronRight,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";

import {
  listEquipmentContextSetpoints,
  updateEquipment,
  setEquipmentConfigParameter,
} from "@/lib/api/building";
import { Input } from "@/components/ui/input";
import { useBuildingChangeTick } from "@/lib/building-changes";
import { AccentRailDisclosure } from "@/components/ui/accent-rail-disclosure";
import {
  getKBDataPointLabels,
  getKBEquipmentType,
  type KBBehavior,
  type KBBehaviorDef,
  type KBBehaviorStatus,
  type KBConfigOptionGroup,
  type ParameterReadiness,
  type ZoneSignal,
} from "@/lib/api/agent";
import {
  buildEquipmentUnlockIndex,
  capabilityCardModelFromInstance,
  groupBehaviorsByHome,
  loadGroups,
  statusMeta,
  type CapGroup,
} from "./CapabilityList.data";
import { ZoneCapabilityBadge } from "./ZoneCapabilityBadge";
import {
  HomePill,
  KbCapabilityCard,
  LayerPill,
} from "@/components/admin/KbCapabilityCard";
import type { KbCapabilityHome, KbCapabilityLayer } from "@/lib/kb-ia";
import { chipsForOptionGroup } from "@/lib/variant-option-chips";
import {
  equipmentTypeIcon,
  equipmentTypeNameFi,
  useEquipmentTypeMeta,
  useKbSensorTypes,
} from "@/lib/equipment-type-meta";
import { equipmentTypeAccent } from "@/lib/equipment-type-accent";
import { dataPointLabel } from "@/lib/data-point-labels";
import {
  cardBase,
  cardSubtle,
  cardSuccess,
  cardSurface,
  cardToneText,
  cardTonePill,
} from "@/lib/card-styles";
import { cn } from "@/lib/utils";

type FilterKey = "all" | "active" | "need";

// A capability "needs attention" when it is not delivering yet — either its signals are unwired
// or it is still waiting on a learned/config parameter (`unready`). Folding both under "need"
// keeps unready capabilities out of the black hole of showing only under "Kaikki".
function needsAttention(status: KBBehaviorStatus): boolean {
  return status === "unwired" || status === "unready";
}

function filterMatch(filter: FilterKey, status: KBBehaviorStatus): boolean {
  if (filter === "all") return true;
  if (filter === "active") return status === "active";
  return needsAttention(status); // "need"
}

// How many of the group's capabilities require this signal — drives the "shared by N" hint.
function sharedByCount(group: CapGroup, dpId: string): number {
  return group.behaviors.filter((b) =>
    b.required_data_points.some((d) => d.id === dpId),
  ).length;
}

// A parameter is "ready" when it currently supplies a value: a learned param in a value-supplying
// state, or a config param an operator has set. The API encodes both as a non-null `value`, so the
// UI reads that rather than re-deriving the learned/config distinction.
function paramReady(p: ParameterReadiness): boolean {
  return p.value !== null;
}

function formatParamValue(value: number, unit: string | null): string {
  const num = Number.isInteger(value)
    ? String(value)
    : value.toLocaleString("fi-FI");
  return unit ? `${num} ${unit}` : num;
}

export function CapabilityList({
  buildingId,
  operator,
  configurator,
  onChanged,
  onGoToSignaalit,
}: {
  buildingId: string;
  operator: boolean;
  // Variant selection is a `configuration`-role action, a higher rung than `operator`.
  // Kept as a separate prop rather than reusing `operator`, which would silently grant
  // operators the ability to change which capabilities an equipment has.
  configurator: boolean;
  onChanged?: () => void;
  /** Deep-link missing signals to Signaalit (declare home) — never write declare here. */
  onGoToSignaalit?: () => void;
}) {
  const eqMeta = useEquipmentTypeMeta();
  const sensors = useKbSensorTypes();
  const { data: dpLabels } = useQuery({
    queryKey: ["kb-data-point-labels"],
    queryFn: getKBDataPointLabels,
    staleTime: Infinity,
  });
  const labels = dpLabels ?? {};
  const [groups, setGroups] = useState<CapGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterKey>("all");
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [reloadKey, setReloadKey] = useState(0);

  const reload = () => setReloadKey((k) => k + 1);

  // Reload behaviours in place (no loading skeleton) after a config/variant write.
  async function refreshGroups() {
    const refreshed = await loadGroups(buildingId).catch(() => null);
    if (refreshed) setGroups(refreshed);
    onChanged?.();
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void loadGroups(buildingId).then((g) => {
      if (cancelled) return;
      setGroups(g);
      // Keep the open group only if it still exists after the reload. A building
      // switch or a removed group collapses — do not auto-open the first remaining.
      setOpenGroup((prev) => {
        const stillPresent = prev !== null && g.some((x) => x.id === prev);
        return stillPresent ? prev : null;
      });
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [buildingId, reloadKey]);

  // Learned parameters or inferences changed in this building: reload the groups in place, so the
  // readiness shown follows the learning sweep without a loading skeleton.
  const learningTick = useBuildingChangeTick(buildingId, "learning");
  useEffect(() => {
    if (learningTick === 0) return;
    let cancelled = false;
    void loadGroups(buildingId)
      .then((refreshed) => {
        if (!cancelled) setGroups(refreshed);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [buildingId, learningTick]);

  function readableType(g: CapGroup): string {
    if (g.kind === "zone") return g.type;
    return equipmentTypeNameFi(eqMeta, g.type, sensors);
  }

  function typeIcon(g: CapGroup): string | null {
    if (g.kind === "zone") return null;
    return equipmentTypeIcon(eqMeta, g.type, sensors);
  }

  const counts = useMemo(() => {
    let active = 0;
    let need = 0;
    let total = 0;
    for (const g of groups) {
      for (const b of g.behaviors) {
        total += 1;
        if (b.status === "active") active += 1;
        else if (needsAttention(b.status)) need += 1;
      }
    }
    return { active, need, total };
  }, [groups]);

  if (loading) {
    return (
      <div
        className={cn(
          cardBase,
          "p-6 text-sm text-muted-foreground animate-pulse",
        )}
      >
        Ladataan kyvykkyyksiä…
      </div>
    );
  }

  return (
    <div className={cn(cardBase, "overflow-hidden")}>
      <div className="flex flex-wrap items-center gap-4 border-b border-border/40 px-4 py-3">
        <Stat value={counts.active} label="aktiivista" />
        <Stat value={counts.need} label="vaatii huomiota" tone="warn" />
        <div
          className={cn(
            cardSurface,
            "border-border/40 ml-auto inline-flex gap-1 p-1",
          )}
        >
          {(
            [
              ["all", "Kaikki"],
              ["active", "Aktiiviset"],
              ["need", "Vaatii huomiota"],
            ] as [FilterKey, string][]
          ).map(([k, lbl]) => (
            <button
              key={k}
              type="button"
              aria-pressed={filter === k}
              onClick={() => setFilter(k)}
              className={cn(
                "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
                filter === k
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {lbl}
            </button>
          ))}
        </div>
      </div>

      {groups.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-muted-foreground">
          Ei kyvykkyyksiä — lisää talotekniikkaa Rakenne-välilehdellä ja valitse
          sen kokoonpano tässä.
        </p>
      ) : (
        <div className="space-y-2 p-3">
          {groups.map((g) => {
            const activeN = g.behaviors.filter(
              (b) => b.status === "active",
            ).length;
            const needN = g.behaviors.filter((b) =>
              needsAttention(b.status),
            ).length;
            const visible = g.behaviors.filter((b) =>
              filterMatch(filter, b.status),
            );
            // Under an active filter, hide stacks with nothing to show — but a group with a
            // load problem (unknown KB type / transient error) always stays visible so the
            // misconfiguration is never silently filtered out (#178).
            if (
              filter !== "all" &&
              visible.length === 0 &&
              g.load.kind === "ok"
            )
              return null;
            const isOpen = openGroup === g.id;
            const icon = typeIcon(g);
            const accent =
              g.kind === "zone"
                ? "color-mix(in oklab, var(--muted-foreground) 45%, transparent)"
                : equipmentTypeAccent(g.type);
            return (
              <AccentRailDisclosure
                key={g.id}
                open={isOpen}
                onToggle={() => setOpenGroup(isOpen ? null : g.id)}
                kind="group"
                accentColor={accent}
                triggerClassName="items-center gap-2.5 px-3 py-2.5 hover:bg-surface/60"
                chevronClassName="h-4 w-4"
                trigger={
                  <>
                    {g.kind === "zone" ? (
                      <Boxes className="h-4 w-4 shrink-0 text-muted-foreground" />
                    ) : icon ? (
                      <span
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-lg leading-none"
                        style={{
                          backgroundColor: `color-mix(in oklab, ${accent} 18%, transparent)`,
                          boxShadow: `inset 0 0 0 1px color-mix(in oklab, ${accent} 45%, transparent)`,
                        }}
                        aria-hidden
                      >
                        {icon}
                      </span>
                    ) : (
                      <Wrench className="h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold">
                        {g.name}
                      </div>
                      <div className="truncate text-[11px] text-muted-foreground">
                        {readableType(g)}
                      </div>
                    </div>
                    {g.load.kind === "ok" ? (
                      <span className="shrink-0 text-[11px] text-muted-foreground">
                        <b className={cn("font-mono", cardToneText.ok)}>
                          {activeN}
                        </b>{" "}
                        aktiivista
                        {needN > 0 && (
                          <>
                            {" · "}
                            <b className={cn("font-mono", cardToneText.warn)}>
                              {needN}
                            </b>{" "}
                            vaatii huomiota
                          </>
                        )}
                      </span>
                    ) : (
                      <span
                        className={cn(
                          "shrink-0 rounded-full border px-2 py-0.5 text-[10.5px]",
                          cardTonePill.warn,
                        )}
                      >
                        {g.load.kind === "unknown_type"
                          ? "Tuntematon tyyppi"
                          : "Latausvirhe"}
                      </span>
                    )}
                  </>
                }
              >
                {isOpen && g.kind === "equipment" && (
                  <VariantSelector
                    buildingId={buildingId}
                    equipmentId={g.id}
                    equipmentType={g.type}
                    declaredVariants={g.variants}
                    configurator={configurator}
                    onChanged={(next) => {
                      setGroups((prev) =>
                        prev.map((x) =>
                          x.id === g.id ? { ...x, variants: next } : x,
                        ),
                      );
                      void reload();
                    }}
                  />
                )}

                {isOpen && (
                  <div className="space-y-1.5 border-t border-border/40 px-3 py-2.5">
                    {g.load.kind === "unknown_type" ? (
                      <div
                        className={cn(
                          "flex items-start gap-2 rounded-md border px-3 py-2 text-xs",
                          cardTonePill.warn,
                        )}
                      >
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span>
                          Tuntematon talotekniikkatyyppi:{" "}
                          <code className="font-mono">{g.type}</code> — vaihda
                          tyyppi Rakenne-välilehdellä.
                        </span>
                      </div>
                    ) : g.load.kind === "error" ? (
                      <div
                        className={cn(
                          "flex items-start gap-2 rounded-md border px-3 py-2 text-xs",
                          cardTonePill.warn,
                        )}
                      >
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span>
                          Kyvykkyyksien lataus epäonnistui — yritä myöhemmin
                          uudelleen.
                        </span>
                      </div>
                    ) : (
                      <CapabilityGroupBody
                        group={g}
                        filter={filter}
                        labels={labels}
                        operator={operator}
                        buildingId={buildingId}
                        expanded={expanded}
                        onToggleExpanded={(key) =>
                          setExpanded((p) => ({ ...p, [key]: !p[key] }))
                        }
                        onGoToSignaalit={onGoToSignaalit}
                        onConfigSaved={() => void refreshGroups()}
                      />
                    )}
                  </div>
                )}
              </AccentRailDisclosure>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Stat({
  value,
  label,
  tone,
}: {
  value: number;
  label: string;
  tone?: "warn";
}) {
  return (
    <span className="text-xs text-muted-foreground">
      <b
        className={cn(
          "font-mono text-sm",
          tone === "warn" ? cardToneText.warn : "text-foreground",
        )}
      >
        {value}
      </b>{" "}
      {label}
    </span>
  );
}

// The capability list body for one group's open disclosure. Equipment groups render home-aware
// sections (story #926 task #938 — komponentti/polku/laite/riippuvuus, consistent with the
// Tietopohja type view); zones keep the flat list + unlock hints unchanged (task #939's job).
function CapabilityGroupBody({
  group,
  filter,
  labels,
  operator,
  buildingId,
  expanded,
  onToggleExpanded,
  onGoToSignaalit,
  onConfigSaved,
}: {
  group: CapGroup;
  filter: FilterKey;
  labels: Record<string, string>;
  operator: boolean;
  buildingId: string;
  expanded: Record<string, boolean>;
  onToggleExpanded: (key: string) => void;
  onGoToSignaalit?: () => void;
  onConfigSaved: () => void;
}) {
  // The ids of this equipment's context-setpoint (asetusarvo) families — Signaalit is their one
  // editable home (story #1515/#1530). A "config" parameter row whose id is in this set must not
  // render an edit affordance here, even though it otherwise qualifies (`param.source ===
  // "config"`). Fetched lazily (this body only mounts once its group's disclosure is open) and
  // stays empty for a zone group, which declares no parameters.
  const [contextSetpointIds, setContextSetpointIds] = useState<
    ReadonlySet<string>
  >(new Set());
  useEffect(() => {
    if (group.kind !== "equipment") return;
    let cancelled = false;
    listEquipmentContextSetpoints(buildingId, group.id)
      .then((cs) => {
        if (!cancelled) {
          setContextSetpointIds(new Set(cs.map((c) => c.context_setpoint_id)));
        }
      })
      .catch(() => {
        if (!cancelled) setContextSetpointIds(new Set());
      });
    return () => {
      cancelled = true;
    };
  }, [buildingId, group.kind, group.id]);

  if (group.kind === "zone") {
    const visible = group.behaviors.filter((b) =>
      filterMatch(filter, b.status),
    );
    return (
      <>
        {group.behaviors.length === 0 ? (
          <p className="py-2 text-center text-xs text-muted-foreground">
            Ei määriteltyjä kyvykkyyksiä tälle tyypille.
          </p>
        ) : visible.length === 0 ? (
          <p className="py-2 text-center text-xs text-muted-foreground">
            Ei kyvykkyyksiä tällä suodattimella.
          </p>
        ) : (
          // A single section, same header rhythm as the equipment path's home sections (story
          // #926 task #939 — IA consistency) — zones have no KB home to group by, so this is one
          // flat section rather than several, not a different visual language.
          <div className="space-y-1.5">
            <div className="flex items-baseline gap-1.5 pt-1 text-[10px] font-semibold uppercase tracking-[0.13em] text-muted-foreground/70">
              <span>Vyöhykkeen kyvykkyydet</span>
            </div>
            <div className="space-y-1.5">
              {visible.map((b) => (
                <CapabilityRow
                  key={`${group.id}:${b.id}`}
                  group={group}
                  behavior={b}
                  labels={labels}
                  operator={operator}
                  buildingId={buildingId}
                  expanded={!!expanded[`${group.id}:${b.id}`]}
                  onToggle={() => onToggleExpanded(`${group.id}:${b.id}`)}
                  onGoToSignaalit={onGoToSignaalit}
                  onConfigSaved={onConfigSaved}
                  contextSetpointIds={contextSetpointIds}
                />
              ))}
            </div>
          </div>
        )}
        <ZoneUnlockHints signals={group.signals} />
      </>
    );
  }

  // Join the instance's applicable behaviours onto the type's home/layer catalog (UC7), then
  // apply the active-filter per row. A hardware-gated row (no instance — UC4) only shows under
  // "Kaikki": it is neither active nor "needs attention", so it has no place in the other two.
  const sections = groupBehaviorsByHome(
    group.behaviors,
    group.typeBehaviors,
    group.typeComponents,
    group.typeStreams,
  );
  const visibleSections = sections
    .map((s) => ({
      ...s,
      rows: s.rows.filter((r) =>
        r.instance ? filterMatch(filter, r.instance.status) : filter === "all",
      ),
    }))
    .filter((s) => s.rows.length > 0);

  const mainContent =
    group.typeBehaviors.length === 0 ? (
      <p className="py-2 text-center text-xs text-muted-foreground">
        Ei määriteltyjä kyvykkyyksiä tälle tyypille.
      </p>
    ) : visibleSections.length === 0 ? (
      <p className="py-2 text-center text-xs text-muted-foreground">
        Ei kyvykkyyksiä tällä suodattimella.
      </p>
    ) : (
      visibleSections.map((section) => (
        <div key={section.componentId ?? section.home} className="space-y-1.5">
          <div className="flex items-baseline gap-1.5 pt-1 text-[10px] font-semibold uppercase tracking-[0.13em] text-muted-foreground/70">
            <span>{section.componentLabel ?? section.homeLabel}</span>
            {section.componentLabel && (
              <span className="normal-case font-normal tracking-normal text-muted-foreground/50">
                · {section.homeLabel}
              </span>
            )}
          </div>
          <div className="space-y-1.5">
            {section.rows.map((row) =>
              row.instance ? (
                <CapabilityRow
                  key={`${group.id}:${row.def.id}`}
                  group={group}
                  behavior={row.instance}
                  home={section.home}
                  layer={row.def.layer ?? "monitoring"}
                  labels={labels}
                  operator={operator}
                  buildingId={buildingId}
                  expanded={!!expanded[`${group.id}:${row.def.id}`]}
                  onToggle={() => onToggleExpanded(`${group.id}:${row.def.id}`)}
                  onGoToSignaalit={onGoToSignaalit}
                  onConfigSaved={onConfigSaved}
                  contextSetpointIds={contextSetpointIds}
                />
              ) : (
                <HardwareGatedRow
                  key={`${group.id}:${row.def.id}`}
                  def={row.def}
                  home={section.home}
                  labels={labels}
                />
              ),
            )}
          </div>
        </div>
      ))
    );

  return (
    <>
      {mainContent}
      <EquipmentUnlockHints
        group={group}
        labels={labels}
        onGoToSignaalit={onGoToSignaalit}
      />
    </>
  );
}

// A type-catalog capability this equipment instance cannot evaluate — its home component/variant
// isn't declared, so signal wiring cannot unlock it (UC4, honest hardware gate). Read-only: no
// wiring affordance, no status badge (it has neither an active nor a blocked *state* — it simply
// doesn't apply yet).
function HardwareGatedRow({
  def,
  home,
  labels,
}: {
  def: KBBehaviorDef;
  home: KbCapabilityHome;
  labels: Record<string, string>;
}) {
  return (
    <div className={cn(cardSubtle, "px-3 py-2.5 opacity-70")}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[150px]">
          <div className="text-sm font-medium">{def.name_fi}</div>
          {def.description_fi && (
            <div className="text-[11px] text-muted-foreground">
              {def.description_fi}
            </div>
          )}
        </div>
        <LayerPill layer={def.layer ?? "monitoring"} />
        <HomePill home={home} />
        <span
          className={cn(
            "shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium",
            cardTonePill.empty,
          )}
          title="Vaatii kokoonpanon — kytke tarvittava kokoonpanovalinta Kokoonpano-osiossa"
        >
          vaatii kokoonpanon
        </span>
      </div>
      {def.required_data_points.length > 0 && (
        <div className="mt-1.5 text-[10.5px] text-muted-foreground/80">
          Mittaukset:{" "}
          {def.required_data_points
            .map((d) => dataPointLabel(d.id, labels))
            .join(" · ")}
        </div>
      )}
    </div>
  );
}

// Zones are read-only (no wiring path yet), so a missing zone signal is otherwise a dead end.
// This surfaces what wiring each unwired signal would unlock — the equipment behaviour(s) from
// the zone type's `enables[]` — turning the flat list into a prioritisation aid. Read-only: no
// toggle affordance. Renders nothing when no unwired signal carries an unlock hint (no empty header).
function ZoneUnlockHints({ signals }: { signals: ZoneSignal[] }) {
  const unlockable = signals.filter((s) => !s.wired && s.enables.length > 0);
  if (unlockable.length === 0) return null;

  return (
    <div className="mt-1.5 border-t border-dashed border-border/40 pt-2.5">
      <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.13em] text-muted-foreground/70">
        Vapauta lisää kyvykkyyksiä
      </div>
      <div className="space-y-1.5">
        {unlockable.map((s) => (
          <div key={s.id} className={cn(cardSurface, "px-2.5 py-2")}>
            <div className="flex items-center gap-2">
              <span className="text-[12px] font-medium">{s.name_fi}</span>
              <span className="font-mono text-[10.5px] text-muted-foreground">
                {s.id}
              </span>
              <span className="text-[10.5px] text-muted-foreground/80">
                kytkemällä vapautuu:
              </span>
            </div>
            <div className="mt-1.5 space-y-1.5">
              {s.enables.map((e) => (
                <div
                  key={`${e.equipment_type}:${e.behavior_id}`}
                  className="flex flex-wrap items-start gap-1.5"
                >
                  <span
                    className={cn(
                      "mt-0.5 shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium",
                      cardTonePill.learning,
                    )}
                  >
                    {e.behavior_name_fi} · {e.equipment_name_fi}
                  </span>
                  {e.description_fi && (
                    <span className="min-w-[140px] flex-1 text-[10.5px] text-muted-foreground">
                      {e.description_fi}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Signal-driven unlock reading — the equipment counterpart to `ZoneUnlockHints`. Promoted from a
// plain footer hint to a first-class, collapsible lens (story #1160 task #1163): "if I activate
// this signal, these capabilities open." Still purely informational — the only write path is the
// "Avaa Signaalit →" deep-link (declare stays owned by Signaalit, story #1145); no capability
// toggle, no local declare write. Driven entirely by data already on the group (client-side
// `buildEquipmentUnlockIndex` over `required_data_points[].wired`) — no new endpoint, no new
// store. Starts collapsed like the per-capability signal/parameter menus (§2.4 locked decision);
// the trigger summarizes signal + capability counts so it stays scannable unopened.
function EquipmentUnlockHints({
  group,
  labels,
  onGoToSignaalit,
}: {
  group: CapGroup;
  labels: Record<string, string>;
  onGoToSignaalit?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const unlocks = useMemo(
    () => [...buildEquipmentUnlockIndex(group.behaviors).entries()],
    [group.behaviors],
  );
  if (unlocks.length === 0) return null;
  const wireable = new Set(group.wireableDataPointIds);
  const unlockedCapabilityCount = new Set(
    unlocks.flatMap(([, enables]) => enables.map((e) => e.behaviorId)),
  ).size;

  return (
    <div className="mt-1.5">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <div className="text-[10px] font-semibold uppercase tracking-[0.13em] text-muted-foreground/70">
          Signaalit avaavat lisää kyvykkyyksiä
        </div>
        {onGoToSignaalit && (
          <button
            type="button"
            onClick={onGoToSignaalit}
            className="text-[10.5px] font-medium text-primary hover:underline"
          >
            Avaa Signaalit →
          </button>
        )}
      </div>
      <AccentRailDisclosure
        open={open}
        onToggle={() => setOpen((o) => !o)}
        kind="chain"
        triggerClassName="items-center gap-2 px-2.5 py-2 hover:bg-surface/60"
        chevronClassName="h-3.5 w-3.5"
        trigger={
          <span className="text-[11px] text-muted-foreground">
            <b className="font-mono text-foreground">{unlocks.length}</b>{" "}
            {unlocks.length === 1 ? "signaali" : "signaalia"} avaisi{" "}
            <b className="font-mono text-foreground">
              {unlockedCapabilityCount}
            </b>{" "}
            {unlockedCapabilityCount === 1 ? "kyvykkyyden" : "kyvykkyyttä"}
          </span>
        }
      >
        {open && (
          <div className="space-y-1.5 border-t border-border/40 px-2.5 py-2">
            {unlocks.map(([dpId, enables]) => {
              const canWire = wireable.has(dpId);
              return (
                <div key={dpId} className={cn(cardSurface, "px-2.5 py-2")}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[12px] font-medium">
                      {dataPointLabel(dpId, labels)}
                    </span>
                    <span className="font-mono text-[10.5px] text-muted-foreground">
                      {dpId}
                    </span>
                    {canWire ? (
                      <span className="text-[10.5px] text-muted-foreground/80">
                        avaamalla Signaaleissa vapautuu:
                      </span>
                    ) : (
                      <span className="text-[10.5px] text-muted-foreground/80">
                        johdettu suure — tee valmiiksi lähdesignaaleista
                      </span>
                    )}
                  </div>
                  <div className="mt-1.5 space-y-1.5">
                    {enables.map((e) => (
                      <div
                        key={e.behaviorId}
                        className="flex flex-wrap items-start gap-1.5"
                      >
                        <span
                          className={cn(
                            "mt-0.5 shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium",
                            cardTonePill.learning,
                          )}
                        >
                          {e.behaviorNameFi}
                        </span>
                        {e.descriptionFi && (
                          <span className="min-w-[140px] flex-1 text-[10.5px] text-muted-foreground">
                            {e.descriptionFi}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </AccentRailDisclosure>
    </div>
  );
}

function CapabilityRow({
  group,
  behavior,
  home,
  layer,
  labels,
  operator,
  buildingId,
  expanded,
  onToggle,
  onGoToSignaalit,
  onConfigSaved,
  contextSetpointIds,
}: {
  group: CapGroup;
  behavior: KBBehavior;
  // Tietopohja-consistent placement chips (story #926 task #938, UC7) — equipment only. Omitted
  // (no chips rendered) for a zone row: zone criteria carry no KB home/layer placement, and
  // forbids fabricating a signal a real endpoint can't supply.
  home?: KbCapabilityHome;
  layer?: KbCapabilityLayer;
  labels: Record<string, string>;
  operator: boolean;
  buildingId: string;
  expanded: boolean;
  onToggle: () => void;
  onGoToSignaalit?: () => void;
  onConfigSaved: () => void;
  /** context_setpoint_id set — Signaalit owns those, never this row (story #1515/#1530). */
  contextSetpointIds: ReadonlySet<string>;
}) {
  // Config values are operator-entered and only on equipment (zone behaviours declare no
  // parameters). A viewer sees the value read-only.
  const editableConfig = operator && group.kind === "equipment";
  const meta = statusMeta(behavior);
  const req = behavior.required_data_points;
  const wiredN = req.filter((d) => d.wired).length;
  const params = behavior.parameters;
  const paramReadyN = params.filter(paramReady).length;
  // Signal + parameter menus start collapsed (story #1160 task #1162) — auto-opening on an
  // unready/partial capability buried the operator under a wall of expanded rows on new devices.
  const [paramsOpen, setParamsOpen] = useState(false);

  // Equipment capabilities render through the shared Tietopohja capability-card contract with an
  // instance-readiness overlay (§2.4, story #1160 tasks #1161/#1162) — one card schema at every
  // home, instance and type view alike, replacing the bespoke title/pill header below. Zone
  // criteria keep the pre-existing flat layout unchanged: they carry no KB home/layer placement
  // and read status from `ZoneCapabilityBadge`, not the readiness vocabulary.
  const cardModel =
    home && layer
      ? capabilityCardModelFromInstance(behavior, home, layer)
      : null;
  // Under Kaikki, an active capability must be obviously distinguishable, not only findable via
  // the Aktiiviset filter (§2.4) — a success-tinted container, not a new chip.
  const isActive = cardModel?.readiness === "active";

  const signalAndParamToggles = (
    <>
      <button
        type="button"
        onClick={onToggle}
        className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-2 py-0.5 text-[10.5px] text-muted-foreground hover:border-primary/40 hover:text-primary"
      >
        signaalit {wiredN}/{req.length}
        <ChevronRight
          className={cn(
            "h-3 w-3 transition-transform",
            expanded && "rotate-90",
          )}
        />
      </button>
      {params.length > 0 && (
        <button
          type="button"
          onClick={() => setParamsOpen((o) => !o)}
          className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-2 py-0.5 text-[10.5px] text-muted-foreground hover:border-primary/40 hover:text-primary"
        >
          parametrit {paramReadyN}/{params.length}
          <ChevronRight
            className={cn(
              "h-3 w-3 transition-transform",
              paramsOpen && "rotate-90",
            )}
          />
        </button>
      )}
      {/* Zone-only: the shared card's readiness pill already carries the partially_ready nuance
          (kb-ia.ts readinessFromBehaviorStatus) for equipment, so this stays zone-only to avoid a
          redundant/contradictory second badge. */}
      {!cardModel && behavior.partially_ready && (
        <span
          className={cn(
            "shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium",
            cardTonePill.learning,
          )}
          title="Osa kriteereistä odottaa parametria — kyvykkyys toimii vain osittain"
        >
          osittain
        </span>
      )}
      {!cardModel &&
        (behavior.capability ? (
          <ZoneCapabilityBadge capability={behavior.capability} />
        ) : (
          <span
            className={cn(
              "shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium",
              cardTonePill[meta.tone],
            )}
          >
            {meta.label}
          </span>
        ))}
    </>
  );

  return (
    <div className={cn(isActive ? cardSuccess : cardSubtle, "px-3 py-2.5")}>
      {cardModel ? (
        <>
          <KbCapabilityCard model={cardModel} />
          <div className="mt-1.5 flex flex-wrap items-center gap-3">
            {signalAndParamToggles}
          </div>
        </>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[150px]">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-sm font-medium">{behavior.name_fi}</span>
              {layer && <LayerPill layer={layer} />}
              {home && <HomePill home={home} />}
            </div>
            {behavior.description_fi && (
              <div className="text-[11px] text-muted-foreground">
                {behavior.description_fi}
              </div>
            )}
          </div>
          {signalAndParamToggles}
        </div>
      )}

      {expanded && (
        <div className="mt-2.5 border-t border-dashed border-border/40 pt-2.5">
          <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.13em] text-muted-foreground/70">
            Vaaditut signaalit
          </div>
          {group.kind === "zone" && (
            <p className="mb-1.5 text-[10.5px] text-muted-foreground/80">
              Vyöhykkeen signaalit ovat vain luku — ne tulevat liitetyiltä
              antureilta eivätkä ole kytkettävissä täällä.
            </p>
          )}
          <div className="space-y-1.5">
            {req.map((d) => {
              const shared = sharedByCount(group, d.id);
              return (
                <div
                  key={d.id}
                  className={cn(
                    cardSurface,
                    "flex items-center gap-3 px-2.5 py-1.5",
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-[12px] font-medium">
                      {dataPointLabel(d.id, labels)}
                    </div>
                    <div className="font-mono text-[10.5px] text-muted-foreground">
                      {d.id}
                    </div>
                    {shared > 1 && (
                      <div className="text-[10.5px] text-muted-foreground/80">
                        Käytössä {shared} kyvykkyydessä
                      </div>
                    )}
                  </div>
                  {d.wired ? (
                    <span
                      className={cn(
                        "shrink-0 rounded-full border px-2 py-0.5 text-[10.5px]",
                        cardTonePill.ok,
                      )}
                    >
                      <CheckCircle2 className="mr-1 inline h-3 w-3" />
                      käytössä
                    </span>
                  ) : (
                    <div className="flex shrink-0 items-center gap-2">
                      <span
                        className={cn(
                          "rounded-full border px-2 py-0.5 text-[10.5px]",
                          cardTonePill.warn,
                        )}
                      >
                        <AlertTriangle className="mr-1 inline h-3 w-3" />
                        vaatii signaalin
                      </span>
                      {group.kind === "equipment" && onGoToSignaalit && (
                        <button
                          type="button"
                          onClick={onGoToSignaalit}
                          className="rounded-full border border-primary/40 bg-primary/5 px-2.5 py-0.5 text-[10.5px] font-medium text-primary hover:bg-primary/10"
                        >
                          Signaalit →
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {paramsOpen && params.length > 0 && (
        <div className="mt-2.5 border-t border-dashed border-border/40 pt-2.5">
          <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.13em] text-muted-foreground/70">
            Vaaditut parametrit
          </div>
          <div className="space-y-1.5">
            {params.map((p) => (
              <ParameterRow
                key={`${p.source}:${p.id}`}
                param={p}
                editableConfig={editableConfig && !contextSetpointIds.has(p.id)}
                buildingId={buildingId}
                equipmentId={group.id}
                onConfigSaved={onConfigSaved}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// One required-parameter row. A config param an operator may set renders a numeric input (no
// progress affordance — a human, not time, resolves it); everyone else sees a read-out: learning
// progress, or the learned/set value + when it was learned. The learned/config distinction is
// read from the API, never re-derived.
function ParameterRow({
  param,
  editableConfig,
  buildingId,
  equipmentId,
  onConfigSaved,
}: {
  param: ParameterReadiness;
  editableConfig: boolean;
  buildingId: string;
  equipmentId: string;
  onConfigSaved: () => void;
}) {
  const ready = paramReady(param);
  const showInput = param.source === "config" && editableConfig;
  return (
    <div className={cn(cardSurface, "flex items-center gap-3 px-2.5 py-1.5")}>
      <div className="min-w-0 flex-1">
        <div className="text-[12px] font-medium">{param.name_fi}</div>
        <div className="font-mono text-[10.5px] text-muted-foreground">
          {param.id}
        </div>
        {ready && param.last_learned_at && (
          <div className="text-[10.5px] text-muted-foreground/80">
            Opittu {new Date(param.last_learned_at).toLocaleDateString("fi-FI")}
          </div>
        )}
      </div>
      {showInput ? (
        <ConfigParameterInput
          param={param}
          buildingId={buildingId}
          equipmentId={equipmentId}
          onSaved={onConfigSaved}
        />
      ) : (
        <ParameterReadout param={param} />
      )}
    </div>
  );
}

// Operator input for a config parameter. Distinct from a learning read-out on purpose: a config
// value never resolves on its own, so it gets an input and a save action, not a progress bar. The
// KB's bounds are enforced server-side; a rejected value's reason is shown inline rather than
// silently dropped.
function ConfigParameterInput({
  param,
  buildingId,
  equipmentId,
  onSaved,
}: {
  param: ParameterReadiness;
  buildingId: string;
  equipmentId: string;
  onSaved: () => void;
}) {
  const [value, setValue] = useState(
    param.value !== null ? String(param.value) : "",
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    const num = Number(value);
    if (value.trim() === "" || Number.isNaN(num)) {
      setError("Anna numeroarvo");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await setEquipmentConfigParameter(buildingId, equipmentId, param.id, num);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Tallennus epäonnistui");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex shrink-0 flex-col items-end gap-1">
      <div className="flex items-center gap-1.5">
        <Input
          type="number"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setError(null);
          }}
          placeholder="—"
          aria-label={`${param.name_fi} — arvo`}
          wrapperClassName="w-24"
          className="px-2 py-1 text-right text-[12px] tabular-nums"
        />
        {param.unit && (
          <span className="text-[10.5px] text-muted-foreground">
            {param.unit}
          </span>
        )}
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving}
          className="shrink-0 rounded-full border border-primary/40 bg-primary/5 px-2.5 py-1 text-[10.5px] font-medium text-primary hover:bg-primary/10 disabled:opacity-50"
        >
          {saving ? "…" : "Tallenna"}
        </button>
      </div>
      {error && (
        <span className="max-w-[220px] text-right text-[10.5px] text-destructive">
          {error}
        </span>
      )}
    </div>
  );
}

// The right-hand read-out for a parameter. A ready value (learned or set) reads in the `ok` tone;
// a learning param shows its sample progress in the `learning` tone; a missing config value has no
// progress affordance — nothing is in progress, only a human resolves it.
function ParameterReadout({ param }: { param: ParameterReadiness }) {
  if (param.value !== null) {
    return (
      <span
        className={cn(
          "shrink-0 rounded-full border px-2 py-0.5 text-[10.5px] font-medium tabular-nums",
          cardTonePill.ok,
        )}
      >
        {formatParamValue(param.value, param.unit)}
      </span>
    );
  }
  if (param.source === "config") {
    return (
      <span
        className={cn(
          "shrink-0 rounded-full border px-2 py-0.5 text-[10.5px]",
          cardTonePill.warn,
        )}
      >
        arvo puuttuu
      </span>
    );
  }
  return (
    <span
      className={cn(
        "shrink-0 rounded-full border px-2 py-0.5 text-[10.5px] tabular-nums",
        cardTonePill.learning,
      )}
      title="Oppii — täydentyy näytteiden karttuessa"
    >
      {param.samples ?? 0}/{param.samples_required ?? 0} näytettä
    </span>
  );
}

// Variant selection — the master switch for which capabilities an equipment has at all.
// It lives here rather than in Rakenne because its effect is visible here: changing it changes
// the capability list directly below it (docs/spec/concepts.md §2.6).
//
// Gated on `configurator`, NOT `operator`: per docs/spec/concepts.md this is a
// `configuration`-role action, and CapabilityList's existing `operator` prop is also true for
// plain operators. Reusing it would silently widen the privilege.
function VariantSelector({
  buildingId,
  equipmentId,
  equipmentType,
  declaredVariants,
  configurator,
  onChanged,
}: {
  buildingId: string;
  equipmentId: string;
  equipmentType: string;
  declaredVariants: string[];
  configurator: boolean;
  onChanged: (next: string[]) => void;
}) {
  const [saving, setSaving] = useState(false);
  const { data: kbType } = useQuery({
    queryKey: ["kb-equipment-type", equipmentType],
    queryFn: () => getKBEquipmentType(equipmentType),
    staleTime: Infinity,
  });

  const groups = kbType?.config_option_groups ?? [];
  if (groups.length === 0) return null;

  async function select(group: KBConfigOptionGroup, choiceId: string) {
    if (saving || !configurator) return;
    // Boolean groups toggle independently; enum groups are exclusive within their own choices,
    // so selecting one clears its siblings but leaves other groups untouched.
    const next =
      group.type === "boolean"
        ? declaredVariants.includes(choiceId)
          ? declaredVariants.filter((v) => v !== choiceId)
          : [...declaredVariants, choiceId]
        : (() => {
            const siblings = new Set(group.choices.map((c) => c.id));
            const others = declaredVariants.filter((v) => !siblings.has(v));
            return declaredVariants.includes(choiceId)
              ? others
              : [...others, choiceId];
          })();

    setSaving(true);
    try {
      await updateEquipment(buildingId, equipmentId, {
        declared_variants: next,
      });
      onChanged(next);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="border-t border-border/40 px-3 py-2.5">
      <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.13em] text-muted-foreground/70">
        Kokoonpano
      </div>
      <div className="space-y-2">
        {groups.map((group) => (
          <div key={group.id}>
            <div className="mb-1 text-[11px] text-muted-foreground">
              {group.name_fi}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {chipsForOptionGroup(group).map((choice) => {
                const selected = declaredVariants.includes(choice.id);
                return (
                  <button
                    key={choice.id}
                    type="button"
                    disabled={!configurator || saving}
                    onClick={() => void select(group, choice.id)}
                    title={choice.description_fi || choice.name_fi}
                    className={cn(
                      "rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors",
                      selected
                        ? "border-primary/40 bg-primary/10 text-primary"
                        : "border-border text-muted-foreground",
                      configurator && !saving
                        ? "hover:border-primary/40 hover:text-primary"
                        : "cursor-default opacity-90",
                    )}
                  >
                    {choice.name_fi}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {!configurator && (
        <p className="mt-1.5 text-[10.5px] text-muted-foreground/70">
          Kokoonpanon muuttaminen vaatii konfiguraattorin oikeudet.
        </p>
      )}
    </div>
  );
}
